import { useState, FormEvent } from "react";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setLoading(true);
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      await fetch("https://readdy.ai/api/form/d75s74t4hjod66j2ef40", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-500 mb-3">Get In Touch</span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
            Let&apos;s Build Your
            <span className="block text-amber-500">Healthcare Facility</span>
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            Ready to equip your hospital with world-class medical infrastructure? Contact our team for a consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-60">
              <iframe
                title="ANDINI Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3062.7!2d32.7!3d39.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d34e3c2b0b8c0d%3A0x0!2sOstim%2C%20Ankara%2C%20Turkey!5e0!3m2!1sen!2str!4v1711000000000!5m2!1sen!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Info Cards */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-amber-500"></i>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Head Office</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Ostim OSB Mah. 244 sok No:27<br />
                    Ostim 06374, Ankara — TURKEY
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-xl text-amber-500"></i>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Phone</div>
                  <a href="tel:+905332663808" className="text-sm text-gray-700 hover:text-amber-500 transition-colors cursor-pointer">
                    +90-533-266 38 08
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-xl text-amber-500"></i>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Email</div>
                  <a href="mailto:mk@andini.com.tr" className="text-sm text-gray-700 hover:text-amber-500 transition-colors cursor-pointer">
                    mk@andini.com.tr
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                  <i className="ri-checkbox-circle-fill text-3xl text-emerald-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm">Thank you for reaching out. Our team will respond within 24 hours.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-5 py-2 rounded-md text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Send Us a Message</h3>
                  <p className="text-sm text-gray-500">We typically respond within 24 hours</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Organization / Hospital</label>
                  <input
                    type="text"
                    name="organization"
                    placeholder="Hospital or company name"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Service Interested In</label>
                  <select
                    name="service"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Hospital Turnkey Project">Hospital Turnkey Project</option>
                    <option value="ICU Equipment">ICU Equipment</option>
                    <option value="Hospital Furniture">Hospital Furniture</option>
                    <option value="Urology Equipment">Urology Equipment</option>
                    <option value="Radiology Equipment">Radiology Equipment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Message * <span className="text-gray-400 font-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your requirements..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  ></textarea>
                  {charCount > 500 && (
                    <p className="text-xs text-red-500 mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full py-3 rounded-lg font-semibold text-sm bg-amber-500 text-white hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
